/* eslint-disable consistent-return */
/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import AdvocacyLanding from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacyLanding';
import AdvocacySuccess from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacySuccess';
import AdvocacyError from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacyError/AdvocacyError';
import AdvocacyErrorEnhanced from 'components/Campaigns/RwdAdvocacy/AdvocacyViews/AdvocacyErrorEnhanced';
import urlUtils from 'utils/Url';
import userUtils from 'utils/User';
import { getContent } from 'services/api/Content/getContent';
import biProfileUtils from 'utils/BiProfile';
import advocacyApi from 'services/api/advocacy';
import decorators from 'utils/decorators';
import {
    CAMPAIGN_TYPE, SHARE, ERROR, PAGE_TYPES
} from 'components/Campaigns/Referrer/constants';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Empty from 'constants/empty';
import BIApi from 'services/api/beautyInsider';

const { LANDING, SUCCESS, ERROR_PAGE } = PAGE_TYPES;

// Classified app-level error codes (ERROR.*, not raw RMS `cmpref.svc.*`) that render the enhanced
// Referee error view (KS-on only). Each maps to a heading + CTA variant inside AdvocacyErrorEnhanced.
// OPT-23061: codes-exhausted; OPT-23096: expired; OPT-23304: referee already registered; OPT-23075: referrer self-registration.
const ENHANCED_ERROR_CODES = [
    ERROR.ERR_CAMP_REF_MAX_COUNT_RCHD,
    ERROR.ERR_CAMP_REF_CMP_EXPIRED,
    ERROR.ERR_CAMP_REFEREE_ALREADY_REGISTERED,
    ERROR.ERR_CAMP_REFERRER_SELF_REGISTRATION_NOT_ALLOWED
];

class RwdAdvocacy extends BaseClass {
    state = {
        isLoading: true,
        errors: null,
        pageType: null,
        contentfulData: null,
        campaignData: null,
        hasCalledEnrolled: false,
        profileStatus: null,
        biAccountId: null,
        referrerCode: null,
        campaignCode: null,
        checksum: null,
        addToPromoSuccess: false
    };

    isFnF = code => {
        return code === CAMPAIGN_TYPE.FNF;
    };

    getUserId = () => {
        return biProfileUtils.getBiAccountId();
    };

    enrollUserSDN = async (enrollPayload, referralToken) => {
        // Prevent duplicate API calls if the promo call has already succeeded
        if (this.state.addToPromoSuccess) {
            return;
        }

        try {
            // Perform the API call to add user to promo
            const response = await decorators.withInterstice(advocacyApi.addToPromotion)(enrollPayload, referralToken);

            // Handle non-200 responseStatus by throwing the error
            if (response.responseStatus !== 200 || response?.errors) {
                // Throw the entire response error to be caught in the catch block
                throw response;
            }

            // If addToPromo was successful (The addToPromo response indicates the user is enrolled)
            if (response.responseStatus === 200 && Boolean(response.enrolled) === true) {
                // OPT-23062: prefer the per-user coupon + dates returned by addToPromotion so
                // the enhanced Success view can render RMS values instead of the static Contentful
                // placeholder. Wrapper aliases clientPromoStartDate/Expiry. Compute once so
                // getCampaignDates() below receives the same effective values that go into state —
                // otherwise passing raw `response` would lose the validate-provided RMS dates when
                // addToPromo returns nulls, and Success would fall back to Contentful dates.
                const prevCampaignData = this.state.campaignData;
                const effectiveCampaignData = {
                    ...prevCampaignData,
                    enrolled: true,
                    couponCode: response?.couponCode ?? prevCampaignData?.couponCode,
                    startDate: response?.startDate ?? prevCampaignData?.startDate,
                    expirationDate: response?.expirationDate ?? prevCampaignData?.expirationDate
                };

                this.setState({
                    hasCalledEnrolled: true,
                    addToPromoSuccess: true,
                    campaignData: effectiveCampaignData
                });

                const userIsBi = userUtils.isBI();

                if (userIsBi) {
                    // enrolled is true from addToPromo and they are BI then show Success page
                    this.getCampaignDates(effectiveCampaignData);

                    return this.setState({ pageType: SUCCESS, isLoading: false });
                } else {
                    // enrolled is true from addToPromo but the user is NON-BI, we need to show them the landing page so they see BI Sign Up Insturctions
                    // before applying the promo
                    return this.setState({ pageType: LANDING, isLoading: false });
                }
            }
        } catch (err) {
            const errorCode = err?.errors?.errorCode || err?.errors?.[0]?.errorCode || ERROR.CUSTOM_ADVOCACY_DOWN;
            this.setErrorState(errorCode);
            this.setState({ addToPromoSuccess: false });
        } finally {
            this.setState({ hasCalledEnrolled: false });
        }
    };

    enrollUser = async (isEnrolled, newReferralToken = null) => {
        const {
            campaignData, campaignCode, referrerCode, checksum, hasCalledEnrolled
        } = this.state;

        if (hasCalledEnrolled || isEnrolled) {
            return;
        }

        // UA-2925: Use new token if provided (from fresh validate call), otherwise fall back to state
        // This ensures we use the latest token with updated user details after login.
        // Null-safe access: state.campaignData can be null when React 18's auto-batching leaves
        // setState({campaignData: data}) uncommitted before enrollUser's sync destructure runs
        // (getCampaignDetailsSDN line 201 → line 213 → validateCampaign → enrollUser).
        const referralToken = newReferralToken || campaignData?.referralToken;

        // This data is needed for RwdAdvocacy calls
        const profileId = Storage.local.getItem(LOCAL_STORAGE.PROFILE_ID);
        let biDataObject = Empty.Object;
        BIApi.getBiProfile(profileId).then(biData => {
            if (biData?.email && biData?.biAccountId) {
                const userDemographicLoginData = {
                    email: biData.email,
                    firstName: biData.firstName || '',
                    lastName: biData.lastName || '',
                    signupDate: biData.signupDate || '',
                    biAccountId: biData.biAccountId
                };

                biDataObject = userDemographicLoginData;
            }

            const {
                email, firstName, lastName, signupDate, biAccountId
            } = biDataObject;

            // To be used elsewhere in this file
            this.setState({ biAccountId });

            const userId = this.getUserId();
            const enrollPayload = {
                referrerCode,
                campaignCode,
                refereeUsaId: userId || biAccountId,
                refereeEmail: userUtils.getProfileEmail() || email,
                refereeFirstName: userUtils.getProfileFirstName() || firstName,
                refereeLastName: userUtils.getProfileLastName() || lastName,
                registrationDate: userUtils.formatUserRegistrationDate() || signupDate
            };

            if (Sephora.configurationSettings.isReferralShareLinkEnhancement) {
                enrollPayload.checksum = checksum;
            }

            this.enrollUserSDN(enrollPayload, referralToken);
        });
    };

    // eslint-disable-next-line consistent-return
    validateCampaign = data => {
        // OPT-23062: prefer the freshly returned validateCampaignReferral payload over
        // this.state.campaignData. React 18 auto-batches the setState({campaignData}) that
        // precedes this call in getCampaignDetailsSDN, so a state read here would be stale (null)
        // on the first pass and route the user through the wrong branch — enrollUser would then
        // fire without a referralToken.
        const campaignData = data || this.state.campaignData || Empty.Object;
        const { enrolled } = campaignData;
        const userIsAnonymous = userUtils.isAnonymous();
        const userIsBi = userUtils.isBI();
        const currentProfileStatus = this.state?.profileStatus;
        const currentBiAccountId = this.state?.biAccountId;

        // Using if else chain so only ONE of the following ever gets executed.
        if (enrolled && userIsBi) {
            // If they are enrolled then return asap
            this.getCampaignDates(campaignData);

            return this.setState({ pageType: SUCCESS, isLoading: false });
        } else if (userIsAnonymous || (!userIsBi && !currentBiAccountId && currentProfileStatus !== 4)) {
            // If the user is anonymous OR if the user is not a BI user, does not have a BI account ID,
            // AND the profile status is not 4
            return this.setState({ pageType: LANDING, isLoading: false });
        } else if (!enrolled && this.state.addToPromoSuccess === false) {
            // SDN/RMS flow for non-enrolled users. Thread the fresh token from the local
            // `campaignData` (which already prefers `data` over stale state above) so we don't
            // read null when React 18 hasn't yet committed the setState({campaignData}) that
            // precedes this call in getCampaignDetailsSDN — same hazard the sibling branch on
            // line 216 already guards against by threading the referralToken.
            this.enrollUser(enrolled, campaignData.referralToken);
        }
    };

    getCampaignDetailsSDN = async payload => {
        // Prevent another call to validate if already successfully enrolled
        // Just show them the barcode
        if (this.state.addToPromoSuccess && this.state?.campaignData?.enrolled) {
            this.setState({
                pageType: SUCCESS,
                isLoading: false
            });

            return;
        }

        try {
            // Await the API call to get the campaign details
            const data = await decorators.withInterstice(advocacyApi.validateCampaignReferral)(payload);
            this.setState({ campaignData: data });

            // This will be true if addToPromo was successful
            // UA-2925: Read enrolled from data response, not state (state may not be updated yet)
            const { enrolled, referralToken } = data;

            // If not yet enrolled, call enrollUser
            if (this.props.auth.profileStatus === 4 && !enrolled && !this.state.addToPromoSuccess) {
                // UA-2925: Pass the new referralToken from response to ensure we use the updated token
                // This prevents using stale token from state before setState completes
                await this.enrollUser(enrolled, referralToken);
            } else {
                // OPT-23062: pass the fresh response so validateCampaign doesn't depend on the
                // preceding setState({campaignData}) being committed (React 18 auto-batching).
                this.validateCampaign(data);
            }
        } catch (err) {
            // OPT-23304: thread the RMS message so the enhanced view can surface the per-campaign
            // discount (e.g. "20% off") instead of a hardcoded value.
            this.setErrorState(err?.errors?.[0]?.errorCode || ERROR.CUSTOM_ADVOCACY_DOWN, err?.errors?.[0]?.message);
        }
    };

    getCampaignDetails = async ({ campaignCode, referrerCode, checksum, biAccountId }) => {
        const refereeUsaId = biAccountId || Storage.local.getItem(LOCAL_STORAGE.BI_ACCOUNT_ID);

        const payload = {
            campaignCode,
            referrerCode,
            checksum,
            refereeUsaId
        };

        this.getCampaignDetailsSDN(payload);
    };

    getContentfulData = ({ campaignCode, referrerCode, checksum, biAccountId }) => {
        const { country, language } = Sephora.renderQueryParams;

        getContent({
            language,
            country,
            path: `/advocacy/${campaignCode}`
        })
            .then(({ data }) => {
                if (data) {
                    if (data.excludeCountry) {
                        this.setErrorState(ERROR.ERR_CAMP_REF_INVALID_COUNTRY);
                    } else {
                        this.setState(
                            {
                                contentfulData: data
                            },
                            () => {
                                this.getCampaignDetails({ campaignCode, referrerCode, checksum, biAccountId });
                            }
                        );
                    }
                } else {
                    this.setErrorState(ERROR.ERR_CAMP_REF_INVALID);
                }
            })
            .catch(_err => {
                this.setErrorState(ERROR.ERR_CAMP_REF_INVALID);
            });
    };

    getAdvocacyContentAndValidate = () => {
        const [page, referrerCode, campaignCode, checksum] = urlUtils.getPathStrings().slice(1);

        this.setState({
            referrerCode,
            campaignCode,
            checksum
        });

        if (page === SHARE && referrerCode && campaignCode) {
            if (this.props.isBiPointsAvailable !== undefined && !this.props.isBiPointsAvailable) {
                this.setErrorState(ERROR.CUSTOM_BI_DOWN);
            } else {
                const { profileStatus: profileStatusSaved, biAccountId: biAccountIdSaved, contentfulData } = this.state;
                const { beautyInsiderAccount: { biAccountId } = {} } = this.props.user;
                const { profileStatus } = this.props.auth;

                // If we have this data already, it means we already evaluated the campaign
                // If so, just return to avoid double evaluation
                if (profileStatus === profileStatusSaved && biAccountId === biAccountIdSaved) {
                    return;
                } else {
                    this.setState({
                        profileStatus,
                        biAccountId
                    });
                }

                const params = {
                    campaignCode,
                    referrerCode,
                    checksum,
                    biAccountId
                };

                if (!contentfulData) {
                    this.getContentfulData(params);
                } else {
                    this.getCampaignDetails(params);
                }
            }
        } else {
            this.setErrorState(ERROR.ERR_CAMP_REF_INVALID);
        }
    };

    componentDidMount() {
        const { isInitialized } = this.props.user || {};

        if (isInitialized) {
            this.getAdvocacyContentAndValidate();
        }
    }

    componentDidUpdate(prevProps) {
        // Don't re-evaluate if we've already reached a terminal state (SUCCESS or ERROR)
        if (this.state.pageType === PAGE_TYPES.SUCCESS || this.state.pageType === PAGE_TYPES.ERROR_PAGE) {
            return;
        }

        if (
            this.props.user?.isInitialized &&
            (!prevProps.user.isInitialized ||
                this.props.auth.profileStatus !== prevProps.auth.profileStatus ||
                prevProps.isBiPointsAvailable !== this.props.isBiPointsAvailable)
        ) {
            this.getAdvocacyContentAndValidate();
        }
    }

    getCampaignDates = (data = {}) => {
        // OPT-23353: Contentful is authoritative for the displayed promo window on both the enhanced
        // and legacy Success views — the RMS (Referral Management System) start/expiration dates are
        // only a fallback when Contentful omits them. (OPT-23062 originally preferred RMS per-user
        // dates for the enhanced view; product reversed that. The per-user coupon code, handled
        // separately, still prefers RMS.)
        const { startDate: rmsStartDate, expirationDate: rmsExpirationDate } = data;
        const { successPromoStartDate, successPromoEndDate } = this.state.contentfulData || {};

        this.setState({
            promoStartDate: successPromoStartDate || rmsStartDate,
            promoEndDate: successPromoEndDate || rmsExpirationDate
        });
    };

    setErrorState = (errorCode, errorMessage = null) => {
        this.setState({
            isLoading: false,
            pageType: ERROR_PAGE,
            errors: errorCode,
            errorMessage
        });
    };

    onBiConversionSuccess = () => {
        // Update state to reflect BI conversion success
        this.setState({ pageType: SUCCESS, isLoading: false });
    };

    render() {
        const {
            globalHeroBanner,
            globalInvitationText,
            referralOfferDescription,
            referralDisclaimer,
            referralInstructions,
            successConfirmationText,
            successInstructions,
            successDiscountDescription,
            successDisclaimer,
            successCtaLink,
            successCouponCode
        } = this.state?.contentfulData || {};

        const {
            promoStartDate, promoEndDate, isLoading, pageType, errors, errorMessage
        } = this.state;

        const { referrerFirstName, campaignType, couponCode: apiCouponCode } = this.state.campaignData || {};

        // Killswitch for the new Referee landing page (OPT-23062). Section-scoped flag; web honors only .isEnabled (minAppVersion is app-only).
        const isEnhanced = Sephora.configurationSettings.refereeLandingPageEnhancements?.isEnabled === true;

        if (isLoading) {
            return null;
        } else {
            if (errors) {
                // OPT-23061/OPT-23096: enhanced Error variant only fires when KS is on and the
                // wrapper classified the RMS response as one of the enhanced cases (codes-exhausted
                // or campaign expired). Wrapper is KS-gated (ADR-0001), so KS-off users never see
                // this branch — legacy `AdvocacyError` output stays byte-identical to today's on
                // rollback. `error` selects the variant (heading + CTAs) inside the view.
                if (isEnhanced && ENHANCED_ERROR_CODES.includes(errors)) {
                    return (
                        <AdvocacyErrorEnhanced
                            error={errors}
                            apiErrorMessage={errorMessage}
                            pageType={pageType}
                            globalHeroBanner={globalHeroBanner}
                            globalInvitationText={globalInvitationText}
                            referrerFirstName={referrerFirstName}
                            referralDisclaimer={referralDisclaimer}
                        />
                    );
                }

                return (
                    <AdvocacyError
                        error={errors}
                        pageType={pageType}
                        isEnhanced={isEnhanced}
                    />
                );
            }

            if (pageType === SUCCESS) {
                return (
                    <AdvocacySuccess
                        globalHeroBanner={globalHeroBanner}
                        globalInvitationText={globalInvitationText}
                        successConfirmationText={successConfirmationText}
                        successInstructions={successInstructions}
                        successDiscountDescription={successDiscountDescription}
                        successDisclaimer={successDisclaimer}
                        successCtaLink={successCtaLink}
                        successPromoStartDate={promoStartDate}
                        successPromoEndDate={promoEndDate}
                        successCouponCode={successCouponCode}
                        apiCouponCode={apiCouponCode}
                        referrerFirstName={referrerFirstName}
                        pageType={pageType}
                        isEnhanced={isEnhanced}
                    />
                );
            }

            if (pageType === LANDING) {
                return (
                    <AdvocacyLanding
                        globalHeroBanner={globalHeroBanner}
                        globalInvitationText={globalInvitationText}
                        referralOfferDescription={referralOfferDescription}
                        referralInstructions={referralInstructions}
                        referralDisclaimer={referralDisclaimer}
                        referrerFirstName={referrerFirstName}
                        campaignType={campaignType}
                        isFnF={this.isFnF(campaignType)}
                        pageType={pageType}
                        onBiConversionSuccess={this.onBiConversionSuccess}
                        isEnhanced={isEnhanced}
                    />
                );
            }
        }

        return null;
    }
}

export default wrapComponent(RwdAdvocacy, 'RwdAdvocacy', true);
